/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */

import { FormattedMessage, useIntl } from 'react-intl';
import { UISwitch, UIFeatherIcon, UISystemIcon, UIChartsIcon } from '@futrli/ui-components';
import clsx from 'clsx';
import { get, noop } from 'lodash';
import React, { FC, memo, useContext, useEffect, useState } from 'react';
import { TRACKING_EVENTS } from '../../../../../core/constants';
import { trackEvent } from '../../../../../core/tracker';
import { PredictionsAccountCategories, VIEW_TYPES } from '../../../../../core/types';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { Actions, AllowedAssumptionTypes } from '../../actions/actions';
import { extractIdsAndTypes, updatePredictionAndPaymentType } from '../../assumptions/settings/settings-modal-utils';
import { getRowHeightSize } from '../../user-grid-settings/user-grid-settings-actions';
import { UserGridSettingsContext } from '../../user-grid-settings/user-grid-settings-context';
import { nestedAccountHeightOverride } from '../style-rows';
import { AssumptionRow } from '../types';
import { MARGIN_LEFT_LEVEL, isAiAssumption } from './header-row-utils';

import grid from '../../../../../ui/data-grid/components/grid.scss';
import styles from './header-row.scss';
import { getColors } from '../style-rows';
import { BASE_COLORS } from '@futrli/colors';
import { ForecastType } from '../../../../onboarding/shared/onboarding-type-select';
import {
  BUDGET_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../../forecast/onboarding/const';
import imageToggleOff from './toggle-off-prediction.png';
import { useAnimationLatency, useOnboardingUrlInfo } from '../../../../../forecast/onboarding/utils/hooks';
import classNames from 'classnames';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { Forecast } from '@futrli-sdk/predict';
import { useParams } from 'react-router-dom';
import Tooltip from 'carbon-react/lib/components/tooltip';

interface Props {
  row: AssumptionRow;
  level: number;
  isLast?: boolean;
  wrapperStyle: React.CSSProperties;
  category?: PredictionsAccountCategories;
  viewType?: VIEW_TYPES;
  isFirstAssumption?: boolean;
  onManualPredictionDrawerOpen: (predictionId?: string, predictionType?: string, isQuickCopy?: boolean) => void;
}

export const HeaderRowAssumption: FC<Props> = memo(
  ({ row, level, isLast, wrapperStyle, category, viewType, isFirstAssumption, onManualPredictionDrawerOpen }) => {
    const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
    const organisationId = tempOrganisationId || '';
    const workspaceId = tempWorkspaceId || '';
    const { addErrorMessage } = useContext(PredictManagementContext);
    const {
      forecast,
      fetchForecastReport,
      isTogglingPrediction,
      setIsTogglingPrediction,
      isConsolidation,
      updateAssumption,
      isReadOnly
    } = usePredictionsInfoContext();
    const { rowHeights } = useContext(UserGridSettingsContext);
    const [excludedFromReport, setExcludedFromReport] = useState(row.account.excluded_from_report);
    const rowHeight = getRowHeightSize(rowHeights);

    const { forecastType, onboardingStep, isOnboardingScenarioWithInvoicesRoute } = useOnboardingUrlInfo();

    const showOnboardingArrow =
      (forecastType === ForecastType.FromLYA && onboardingStep === BUDGET_ONBOARDING_STEP.LYA_TOGGLE_OFF) ||
      (forecastType === ForecastType.WithAutoPredictions &&
        onboardingStep === BUDGET_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED) ||
      (isOnboardingScenarioWithInvoicesRoute &&
        forecastType === ForecastType.FromLYA &&
        onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_TOGGLE_OFF) ||
      (isOnboardingScenarioWithInvoicesRoute &&
        forecastType === ForecastType.WithAutoPredictions &&
        onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_EXPLAINED);

    const showAnimatedArrow = useAnimationLatency(showOnboardingArrow && isFirstAssumption);

    const intl = useIntl();

    useEffect(() => {
      setExcludedFromReport(row.account.excluded_from_report);
    }, [row.account.excluded_from_report]);

    if (!forecast) {
      throw new Error('Missed forecast in order to build the header');
    }

    const assumptionsRowHeightOverride = nestedAccountHeightOverride(row, rowHeight, level);

    const isRowAiAssumption = isAiAssumption(row);

    const isImportedFromAdvisor = !!row.account.imported_from_advisor;
    const isExcelAssumption = !!row.account.is_excel_assumption;

    const categoryClass = category ? { [grid[category]]: true } : {};

    const headerRowClasses = clsx({
      [grid.rowHeader]: true,
      [grid.isChild]: true,
      [grid.isFirst]: row.siblingIndex === 0,
      [grid.isLast]: isLast,
      [grid.isPrediction]: true,
      [grid.isAiAssumption]: isRowAiAssumption,
      ...categoryClass
    });

    const headerRowInnerClasses = clsx({
      [grid.rowHeaderInner]: true,
      [grid[assumptionsRowHeightOverride]]: true,
      [grid.isDerived]: row.account.is_derived,
      [grid[assumptionsRowHeightOverride]]: true,
      [grid.outdented]: true
    });

    const headerTitleClasses = clsx({
      [grid.title]: true,
      [grid.titleDisabled]: excludedFromReport,
      [styles.excelNameIcon]: isExcelAssumption
    });

    const handleUpdatePrediction = async (data: Partial<AllowedAssumptionTypes>) => {
      const [predictionType, predictionId, paymentType] = extractIdsAndTypes(row.account);

      if (!workspaceId || !organisationId || !forecast.id || !fetchForecastReport || !addErrorMessage) {
        return addErrorMessage?.(new Error('Missing parameters for updatePredictionAndPaymentType'));
      }

      await updatePredictionAndPaymentType(
        workspaceId,
        organisationId,
        forecast.id,
        predictionType,
        predictionId,
        paymentType,
        {
          prediction: data
        },
        fetchForecastReport,
        addErrorMessage,
        undefined,
        noop,
        noop
      );
    };

    const updateConsolidatedPrediction = async (data: Partial<AllowedAssumptionTypes>) => {
      if (!workspaceId || !organisationId || !forecast.id || !updateAssumption) {
        return addErrorMessage?.(new Error('Missing parameters for updateConsolidatedPrediction'));
      }
      await updateAssumption(data, workspaceId, organisationId, forecast.id, row.account.index);
    };

    const handleRowToggle = async () => {
      if (isReadOnly) {
        return;
      }

      setIsTogglingPrediction?.(true);
      setExcludedFromReport(!excludedFromReport);

      trackEvent(TRACKING_EVENTS.TOGGLED_PREDICTION_ON_OFF);

      if (isConsolidation) {
        await updateConsolidatedPrediction({ excluded_from_report: !excludedFromReport });
      } else {
        await handleUpdatePrediction({
          excluded_from_report: !excludedFromReport
        });
      }
      setIsTogglingPrediction?.(false);
    };

    const colors = getColors(category || 'income', viewType);

    const confidenceScore = isRowAiAssumption ? get(row, 'account.this_number_explained.confidence_score') : undefined;
    // const summaryText = isRowAiAssumption ? get(row, 'account.this_number_explained.summary_text') : undefined;
    const confidenceScoreColor =
      confidenceScore === 1
        ? '#DB7380'
        : confidenceScore === 2
        ? '#F8B06D'
        : confidenceScore === 3
        ? '#4DA77E'
        : undefined;

    return (
      <div className={headerRowClasses} style={wrapperStyle} role="row">
        {isFirstAssumption && showOnboardingArrow && (
          <img
            aria-hidden="true"
            src={imageToggleOff}
            alt="toggle off/on prediction"
            style={{ position: 'absolute', display: 'flex', left: -8, top: 16, zIndex: 10, width: 40, height: 49 }}
            className={classNames(grid.fadeInContainer, {
              [grid.fadeIn]: showAnimatedArrow
            })}
          />
        )}
        <div
          role="cell"
          className={headerRowInnerClasses}
          style={
            isRowAiAssumption && confidenceScoreColor
              ? {
                  backgroundColor: colors[2],
                  marginLeft: level * MARGIN_LEFT_LEVEL,
                  borderLeft: `3px solid ${confidenceScoreColor}`
                }
              : { backgroundColor: colors[2], marginLeft: level * MARGIN_LEFT_LEVEL }
          }
        >
          <div className={clsx(grid.flexContainer, grid.flexContainerJustify)}>
            {!isReadOnly && !row.account.is_derived && !(forecast as Forecast).is_frozen && (
              <div className={grid.toggle} data-testid="futrli-predicts-toggle">
                <UISwitch
                  checked={!excludedFromReport}
                  disabled={isTogglingPrediction}
                  onChange={handleRowToggle}
                  colorThumb={colors[3]}
                  colorTrack={colors[4]}
                  customColor={BASE_COLORS.mint}
                />
              </div>
            )}
            <div className={headerTitleClasses} data-testid="account-name">
              {isRowAiAssumption ? (
                <React.Fragment>
                  <UIFeatherIcon iconName="trending-up" svgAttributes={{ className: styles.predictedIcon }} />
                  {row.account.is_derived
                    ? intl.formatMessage(
                        {
                          id: 'predict.common.ai_assumption_name',
                          defaultMessage: 'Predicted ({primaryAccountName})'
                        },
                        { primaryAccountName: row.account.primary_account_name }
                      )
                    : intl.formatMessage({ id: 'predict.common.predicted', defaultMessage: 'Predicted' })}
                </React.Fragment>
              ) : isImportedFromAdvisor ? (
                <React.Fragment>
                  <Tooltip
                    message={
                      <FormattedMessage
                        id="predict.imported_from_futrli_advisor"
                        defaultMessage="Imported from Futrli Advisor"
                      />
                    }
                    position="top"
                  >
                    <span>
                      <UISystemIcon
                        iconName="import-from-advisor"
                        width={20}
                        height={20}
                        className={styles.advisorIcon}
                      />
                    </span>
                  </Tooltip>
                  {row.account.name}
                </React.Fragment>
              ) : isExcelAssumption ? (
                <React.Fragment>
                  <Tooltip
                    message={
                      <FormattedMessage
                        id="predict.excel_imported_prediction"
                        defaultMessage="Excel imported prediction"
                      />
                    }
                    position="top"
                  >
                    <span style={{ display: 'flex' }}>
                      <UIChartsIcon iconName="mini-excel-icon" />
                    </span>
                  </Tooltip>
                  {row.account.name}
                </React.Fragment>
              ) : (
                row.account.name
              )}
            </div>
            <div className={grid.rowHeaderActions}>
              {!isReadOnly &&
              !excludedFromReport &&
              !isRowAiAssumption &&
              !row.isSystemAssumption &&
              !row.account.is_excel_assumption ? (
                <Actions
                  accountName={row.parentName}
                  row={row.account}
                  predictionCount={row.siblingCount}
                  rowHeight={assumptionsRowHeightOverride}
                  onManualPredictionDrawerOpen={onManualPredictionDrawerOpen}
                />
              ) : null}
            </div>
            {/* <div style={{ width: 72, borderLeft: '1px solid #F6F7FA', height: '100%' }}></div> */}
          </div>
        </div>
      </div>
    );
  }
);
